import { PositionCoordinateListPanel } from './PositionCoordinateListPanel'
import { PositionSetupCanvasPanel } from './PositionSetupCanvasPanel'
import { PositionDetailPanel } from './PositionDetailPanel'
import type { Position, SlamPoint, TimeRange } from '../types'

interface RobotPositionSetupTabProps {
  robotId: string
  activeTab: string
  showPositionSetupContent: boolean
  showPositionPanel: boolean
  selectedCoordinate: SlamPoint | null
  positionsAtSelectedCoordinate: Position[]
  selectedPositionId: string
  isDeletingLocation: boolean
  hasMap: boolean
  hasSlamLoadError: boolean
  isPositionSetupLoading: boolean
  slamJson: any
  visibleMarkers: any[]
  pendingClickCoords: SlamPoint | null
  directionRotateMode: boolean
  isAddingLocation: boolean
  directionOverlayMarkerId: string | null
  directionOverlayYaw: number | null
  isRetrying: boolean
  retrySeconds: number
  positionPanelTab: 'info' | 'schedule'
  selectedPosition: Position | null
  loadingSchedulePositionId: string
  tabsClassName: string
  editingPositionNameRef: React.MutableRefObject<{
    id: string
    name: string
  } | null>
  isEditingMode: boolean
  mapRotation?: number
  backgroundImageUrl?: string | null
  backgroundWidthM?: number
  backgroundHeightM?: number
  isAddmode: boolean
  isHomePlacementRequired: boolean
  homePlacementPoint: SlamPoint | null
  effectiveSelectedPosition: Position | null
  homeYaw?: number

  // Handlers
  onSelectPosition: (posId: string) => void
  onUpdatePosition: (posId: string) => void
  onRequestDelete: (posId: string) => void
  onRefreshPositions: () => void
  onSlamClick: (p: SlamPoint) => void
  onMarkerClick: (posId: string) => void
  onMarkerEditDirection: (posId: string) => void
  onMarkerYawChange: (posId: string, yaw: number) => void
  onMarkerYawCommit: (posId: string, yaw: number) => void
  onConfirmAddNew: () => void
  onRetrySendMap: () => void
  onClosePositionDetail: () => void
  onPositionPanelTabChange: (tab: any) => void
  onPositionNameChange: (name: string) => void
  onSchedulesChange: () => void
  onLocalTimeRangesChange?: (ranges: TimeRange[]) => void
  formatYawLabel: (yaw: number) => string
  onCancelEdit: () => void
  onSavePosition: () => Promise<void>
  onAddPosition: () => void
  onRepickHome: () => void
  onEditHomeDirection?: () => void
}

export function RobotPositionSetupTab({
  robotId,
  activeTab,
  showPositionSetupContent,
  showPositionPanel,
  selectedCoordinate,
  positionsAtSelectedCoordinate,
  selectedPositionId,
  isDeletingLocation,
  hasMap,
  hasSlamLoadError,
  isPositionSetupLoading,
  slamJson,
  visibleMarkers,
  pendingClickCoords,
  directionRotateMode,
  isAddingLocation,
  directionOverlayMarkerId,
  directionOverlayYaw,
  isRetrying,
  retrySeconds,
  positionPanelTab,
  selectedPosition,
  loadingSchedulePositionId,
  tabsClassName,
  editingPositionNameRef,
  isEditingMode,
  mapRotation,
  backgroundImageUrl,
  backgroundWidthM,
  backgroundHeightM,
  isAddmode,
  isHomePlacementRequired,
  homePlacementPoint,
  effectiveSelectedPosition,
  homeYaw = 0,
  onSelectPosition,
  onUpdatePosition,
  onRequestDelete,
  onRefreshPositions,
  onSlamClick,
  onMarkerClick,
  onMarkerEditDirection,
  onMarkerYawChange,
  onMarkerYawCommit,
  onConfirmAddNew,
  onRetrySendMap,
  onClosePositionDetail,
  onPositionPanelTabChange,
  onPositionNameChange,
  onSchedulesChange,
  onLocalTimeRangesChange,
  formatYawLabel,
  onCancelEdit,
  onSavePosition,
  onAddPosition,
  onRepickHome,
  onEditHomeDirection,
}: RobotPositionSetupTabProps) {
  const currentMarkerId = isAddmode ? 'temp_add_id' : selectedPositionId
  const shouldRotate = activeTab === '2' && (directionRotateMode || isAddmode)
  const overlayMarkerId = isAddmode ? 'temp_add_id' : directionOverlayMarkerId
  const overlayYaw = isAddmode
    ? (effectiveSelectedPosition?.yaw ?? 0)
    : directionOverlayYaw

  // Mở ngay panel khi vừa bấm Chấm lại Home
  const showDetailPanel =
    isAddmode ||
    isHomePlacementRequired ||
    (showPositionSetupContent && !!selectedPositionId)

  return (
    <div className="flex min-h-[600px] flex-col gap-4 p-3 2xl:flex-row">
      {showPositionSetupContent &&
        showPositionPanel &&
        !isHomePlacementRequired && (
          <PositionCoordinateListPanel
            selectedCoordinate={selectedCoordinate}
            positions={positionsAtSelectedCoordinate}
            selectedPositionId={selectedPositionId}
            isDeletingLocation={isDeletingLocation}
            onSelectPosition={onSelectPosition}
            onUpdatePosition={onUpdatePosition}
            onRequestDelete={onRequestDelete}
            onRefresh={onRefreshPositions}
            onAddPosition={onAddPosition}
          />
        )}

      <PositionSetupCanvasPanel
        hasMap={hasMap}
        robotId={robotId}
        selectedCoordinate={selectedCoordinate}
        hasSlamLoadError={hasSlamLoadError}
        isPositionSetupLoading={isPositionSetupLoading}
        showPositionSetupContent={showPositionSetupContent}
        slamJson={slamJson}
        markers={
          isHomePlacementRequired
            ? []
            : isAddmode
              ? effectiveSelectedPosition
                ? [{ ...effectiveSelectedPosition, selected: true }]
                : []
              : visibleMarkers
        }
        pendingMarker={isAddmode ? null : pendingClickCoords}
        selectedMarkerId={currentMarkerId || null}
        rotateMode={shouldRotate}
        rotateMarkerId={currentMarkerId || null}
        isConfirmingAddNew={isAddingLocation}
        directionOverlayMarkerId={overlayMarkerId}
        directionOverlayYaw={overlayYaw}
        isRetrying={isRetrying}
        retrySeconds={retrySeconds}
        mapRotation={mapRotation}
        backgroundImageUrl={backgroundImageUrl}
        backgroundWidthM={backgroundWidthM}
        backgroundHeightM={backgroundHeightM}
        isAddmode={isAddmode}
        homePlacementLock={isHomePlacementRequired}
        homePlacementPoint={homePlacementPoint}
        onSlamClick={onSlamClick}
        onMarkerClick={onMarkerClick}
        onMarkerEditDirection={onMarkerEditDirection}
        onMarkerYawChange={onMarkerYawChange}
        onMarkerYawCommit={onMarkerYawCommit}
        onConfirmAddNew={onConfirmAddNew}
        onRetry={onRetrySendMap}
        onRepickHome={onRepickHome}
      />

      {showDetailPanel && (
        <PositionDetailPanel
          robotId={robotId}
          activeTab={activeTab}
          positionPanelTab={positionPanelTab}
          selectedPosition={selectedPosition}
          loadingSchedulePositionId={loadingSchedulePositionId}
          tabsClassName={tabsClassName}
          editingPositionNameRef={editingPositionNameRef}
          onClose={onClosePositionDetail}
          onTabChange={onPositionPanelTabChange}
          onPositionNameChange={onPositionNameChange}
          onSchedulesChange={onSchedulesChange}
          onLocalTimeRangesChange={onLocalTimeRangesChange}
          formatYawLabel={formatYawLabel}
          isEditable={isEditingMode}
          isHomePlacementRequired={isHomePlacementRequired}
          homeYaw={homeYaw}
          onCancelEdit={onCancelEdit}
          onSave={onSavePosition}
          isAddmode={isAddmode}
          onEditHomeDirection={onEditHomeDirection}
        />
      )}
    </div>
  )
}
